import { useState, useEffect, useRef } from 'react';
import { useNetworkStatus, setUserOfflineMode, type NetworkStatus } from '../../services/networkStatus';
import { triggerOfflineFlush } from '../../hooks/useOfflineSync';
import i18n from '../../i18n';
import './CoverageIndicator.css';

// Mapeo de estados a etiqueta y modificador CSS. Mantenemos los nombres
// "good/weak/deficient/offline" en CSS para no tocar estilos previos.
function statusLabel(s: NetworkStatus): { text: string; cssMod: string } {
  switch (s.kind) {
    case 'fast':
      return {
        text: s.latencyMs != null ? i18n.t('components.coverage.onlineLatency', { ms: s.latencyMs }) : i18n.t('components.coverage.online'),
        cssMod: 'good',
      };
    case 'normal':
      return { text: i18n.t('components.coverage.online'), cssMod: 'good' };
    case 'slow':
      return { text: i18n.t('components.coverage.slow'), cssMod: 'weak' };
    // Esperando la primera respuesta al abrir/volver a la app, o comprobando
    // con la sonda antes de dar por perdida la conexión. No es un fallo: se
    // pinta neutro y con spinner en lugar del punto.
    case 'connecting':
      return { text: i18n.t('components.coverage.connecting'), cssMod: 'connecting' };
    case 'manual_offline':
      return { text: i18n.t('components.coverage.offlineMode'), cssMod: 'offline' };
    case 'offline':
      return { text: i18n.t('components.coverage.noConnection'), cssMod: 'deficient' };
  }
}

export function CoverageIndicator() {
  const status = useNetworkStatus();
  const [popupOpen, setPopupOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  // Cierra el popup al tocar fuera. Implementación liviana — solo escucha
  // mientras el popup está abierto.
  useEffect(() => {
    if (!popupOpen) return;
    const onDocClick = (e: Event) => {
      if (!wrapperRef.current?.contains(e.target as Node)) setPopupOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('touchstart', onDocClick);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('touchstart', onDocClick);
    };
  }, [popupOpen]);

  const { text, cssMod } = statusLabel(status);
  const togglePopup = () => setPopupOpen((p) => !p);

  const goOnline = () => {
    setUserOfflineMode(false);
    // Disparar flush inmediatamente — el toggle manual no emite ningún
    // evento de red, así que sin esto la cola tendría que esperar al
    // siguiente tick del polling.
    triggerOfflineFlush(true);
    setPopupOpen(false);
  };
  const goOffline = () => {
    setUserOfflineMode(true);
    setPopupOpen(false);
  };

  return (
    <div className="coverage-wrap" ref={wrapperRef}>
      <button
        type="button"
        className={`coverage coverage--${cssMod}`}
        onClick={togglePopup}
        aria-label={text}
        aria-haspopup="menu"
        aria-expanded={popupOpen}
      >
        {status.kind === 'connecting'
          ? <span className="coverage__spinner" aria-hidden="true" />
          : <span className="coverage__dot" />}
        <span className="coverage__label">{text}</span>
      </button>

      {popupOpen && (
        <div className="coverage-popup" role="menu">
          <div className="coverage-popup__row">
            <span className="coverage-popup__key">Estado</span>
            <span className="coverage-popup__val">{text}</span>
          </div>
          {status.latencyMs != null && (
            <div className="coverage-popup__row">
              <span className="coverage-popup__key">Latencia</span>
              <span className="coverage-popup__val">{status.latencyMs} ms</span>
            </div>
          )}
          <div className="coverage-popup__divider" />
          {status.kind === 'manual_offline' ? (
            <button type="button" className="coverage-popup__action coverage-popup__action--primary" onClick={goOnline}>
              Volver a intentar online
            </button>
          ) : status.kind === 'offline' ? (
            <div className="coverage-popup__hint">
              Sin red disponible. La app se reconectará en cuanto vuelva.
            </div>
          ) : status.kind === 'connecting' ? (
            <div className="coverage-popup__hint">
              Esperando respuesta del servidor. Mientras tanto ves los datos guardados.
            </div>
          ) : (
            <button type="button" className="coverage-popup__action" onClick={goOffline}>
              Trabajar offline
            </button>
          )}
        </div>
      )}
    </div>
  );
}
